import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, Flex, TextArea } from "@hopper-ui/components";
import { CheckmarkIcon, DismissIcon } from "@hopper-ui/icons";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

interface Props {
    defaultValue: string;
    onSave: (value: string) => void;
    onCancel: () => void;
}

export const SavedMemoryItemEdit = ({ defaultValue, onSave, onCancel }: Props) => {
    const { tShared } = useSharedTranslation();
    const [value, setValue] = useState(defaultValue);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const adjustTextAreaHeight = useCallback(() => {
        const textArea = textAreaRef.current;
        if (textArea) {
            // Reset height to auto to get the correct scrollHeight
            textArea.style.height = "auto";
            // Set the height to the scrollHeight to fit the content
            textArea.style.height = `${textArea.scrollHeight}px`;
        }
    }, [textAreaRef]);

    useEffect(() => {
        // Workaround to ensure the cursor is placed at the end after the component has mounted
        setTimeout(() => {
            const textArea = textAreaRef.current;
            if (textArea) {
                textArea.selectionStart = textArea.selectionEnd = textArea.value.length;
            }
        }, 0);
    }, []);

    useEffect(() => {
        adjustTextAreaHeight();
    }, [value, adjustTextAreaHeight]);

    const handleSave = useCallback(() => {
        const trimmedValue = value.trim();
        if (trimmedValue !== "") {
            onSave(trimmedValue);
        }
    }, [value, onSave]);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === "Escape") {
            onCancel();
        }
    }, [handleSave, onCancel]);

    return (
        <Flex gap="inline-xs" alignItems="center" width="100%">
            <TextArea
                inputRef={textAreaRef}
                value={value}
                onChange={_value => setValue(_value)}
                onKeyDown={handleKeyDown}
                placeholder={tShared("personalization.editMemoryPlaceholder")}
                autoFocus
                resize="none"
                rows={1}
                isFluid
            />
            <Button
                aria-label={tShared("personalization.saveMemory")}
                onPress={handleSave}
                variant="ghost-secondary"
                size="sm"
                flexShrink={0}
            >
                <CheckmarkIcon />
            </Button>
            <Button
                aria-label={tShared("personalization.cancelEdit")}
                onPress={onCancel}
                variant="ghost-secondary"
                size="sm"
                flexShrink={0}
            >
                <DismissIcon />
            </Button>
        </Flex>
    );
};
